import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { BUSINESS, SITE_URL, VERIFICATION } from './businessConfig';
import {
  restaurantSchema, websiteSchema, breadcrumbSchema, menuSchema, faqSchema,
} from './schemas';

/**
 * Drop-in <SEO /> for any page. Injects:
 *   - <title> + <meta description> + canonical
 *   - Open Graph + Twitter card metadata
 *   - JSON-LD structured data (Restaurant + WebSite + Breadcrumbs + optional Menu)
 *   - Crawler verification meta tags (Google / Bing / Yandex / FB)
 *   - `noindex` for private routes
 *
 * The component renders NOTHING visible. It only updates document <head>.
 *
 * Props:
 *   - title:        override the document title (defaults to a high-intent default)
 *   - description:  meta description (defaults to BUSINESS.description)
 *   - noIndex:      set true to emit `<meta name="robots" content="noindex,nofollow">`
 *   - breadcrumbs:  array of { name, url } for BreadcrumbList JSON-LD
 *   - menuItems:    array of live menu items — when present, Menu JSON-LD is emitted
 *   - includeRestaurantSchema: emit Restaurant + WebSite JSON-LD (default true on home)
 */
export const SEO = ({
  title,
  description,
  noIndex = false,
  breadcrumbs = [],
  menuItems = null,
  includeRestaurantSchema = true,
  includeFaqSchema = false,
  ogImage,
}) => {
  const location = useLocation();
  const canonical = `${SITE_URL}${location.pathname}`;

  const pageTitle = title || `${BUSINESS.legalName} — Takeaway & Delivery in Forfar`;
  const pageDesc = description || BUSINESS.description;
  const social = ogImage || BUSINESS.logoUrl;

  // Build the JSON-LD graph for this page.
  const ldGraph = useMemo(() => {
    const graph = [];
    if (includeRestaurantSchema) {
      graph.push(restaurantSchema());
      graph.push(websiteSchema());
    }
    if (breadcrumbs.length) graph.push(breadcrumbSchema(breadcrumbs));
    if (menuItems && menuItems.length) graph.push(menuSchema(menuItems));
    if (includeFaqSchema) graph.push(faqSchema());
    return graph;
  }, [includeRestaurantSchema, includeFaqSchema, breadcrumbs, menuItems]);

  return (
    <Helmet prioritizeSeoTags>
      <html lang="en-GB" />
      <title>{pageTitle}</title>
      <meta name="description" content={pageDesc} />
      {noIndex ? (
        // noindex,follow — keeps the page out of the SERPs but lets
        // link equity from any inbound links still flow to indexable
        // pages we link to (homepage nav, footer links).
        <meta name="robots" content="noindex,follow" />
      ) : (
        <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      )}
      {/* Mirror the robots directive on the googlebot-specific meta.
          Without this, a leftover generic `googlebot=index,follow` on any
          private route would contradict the noindex above and Google
          would pick the LESS restrictive value (index) for its crawler.
          Emitting a matching googlebot tag guarantees Google reads a
          single, consistent signal. */}
      {noIndex ? (
        <meta name="googlebot" content="noindex,follow" />
      ) : (
        <meta name="googlebot" content="index,follow" />
      )}
      <link rel="canonical" href={canonical} />

      {/* Search-engine verification (only renders if env var is set) */}
      {VERIFICATION.google && (
        <meta name="google-site-verification" content={VERIFICATION.google} />
      )}
      {VERIFICATION.bing && (
        <meta name="msvalidate.01" content={VERIFICATION.bing} />
      )}
      {VERIFICATION.yandex && (
        <meta name="yandex-verification" content={VERIFICATION.yandex} />
      )}
      {VERIFICATION.facebook && (
        <meta name="facebook-domain-verification" content={VERIFICATION.facebook} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content="restaurant.restaurant" />
      <meta property="og:site_name" content={BUSINESS.legalName} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDesc} />
      <meta property="og:url" content={canonical} />
      <meta property="og:locale" content="en_GB" />
      <meta property="og:image" content={social} />
      <meta property="og:image:secure_url" content={social} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${BUSINESS.legalName} — Takeaway & Delivery`} />

      {/* Twitter / X */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDesc} />
      <meta name="twitter:image" content={social} />
      <meta name="twitter:image:alt" content={`${BUSINESS.legalName} — Takeaway & Delivery`} />

      {/* Mobile / theme */}
      <meta name="theme-color" content="#dc2626" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-title" content={BUSINESS.name} />
      <meta name="format-detection" content="telephone=yes" />

      {/* JSON-LD structured data */}
      {ldGraph.map((node, i) => (
        <script key={node['@id'] || node['@type'] || `ld-${i}`} type="application/ld+json">
          {JSON.stringify(node)}
        </script>
      ))}
    </Helmet>
  );
};

export default SEO;
